/**
 * Shared `InHouseStatus` ranking used by the Kiosk + Location filters to order
 * result suggestions and bucket Group-by views by lifecycle priority. Source of
 * truth lives in `DVD_Admin.dbo.S_Admin_CB_Values WHERE sName='InHouseStatus'`
 * server-side; the order below mirrors the product rule "LIVE highest →
 * ORDERED lowest" and is the same list the user sees in the kiosk edit form.
 *
 * Unknown / empty values are treated as TEST per the rule for locations that
 * have no child kiosks yet.
 */

export const IN_HOUSE_STATUS_ORDER = [
  "LIVE",
  "INACTIVE",
  "TEST",
  "TRANSFERRED",
  "WAREHOUSE",
  "DELIVERED",
  "ORDERED",
] as const;

export type InHouseStatus = typeof IN_HOUSE_STATUS_ORDER[number];

const RANK: Record<string, number> = Object.fromEntries(
  IN_HOUSE_STATUS_ORDER.map((s, i) => [s, i]),
);

export function rankInHouseStatus(s: string | null | undefined): number {
  const k = (s ?? "").toUpperCase();
  return k in RANK ? RANK[k]! : RANK.TEST!;
}

export function normalizeInHouseStatus(s: string | null | undefined): InHouseStatus {
  const k = (s ?? "").toUpperCase();
  return (k in RANK ? k : "TEST") as InHouseStatus;
}

/** Pick the highest-priority status across a kiosk list. Empty list → TEST. */
export function rollupInHouseStatus(
  kiosks: ReadonlyArray<{ InHouseStatus?: string }>,
): InHouseStatus {
  if (!kiosks.length) return "TEST";
  let best: InHouseStatus = normalizeInHouseStatus(kiosks[0]!.InHouseStatus);
  let bestRank = rankInHouseStatus(best);
  for (let i = 1; i < kiosks.length; i++) {
    const cur = normalizeInHouseStatus(kiosks[i]!.InHouseStatus);
    const r = rankInHouseStatus(cur);
    if (r < bestRank) {
      best = cur;
      bestRank = r;
    }
  }
  return best;
}
